import { compact, map } from "lodash";
import { useMemo } from "react";
import {
  AssessmentDeliveryFragment,
  AssessmentDeliveryTemplateFragment,
  AssessmentGroupDelivery,
  AssessmentQuestionResponseVisibility,
  AssessmentQuestionType,
} from "types/graphql-schema";

import { getAssessmentSections } from "@apps/assessments/helpers";
import useLabel from "@apps/use-label/use-label";
import { currentUserVar } from "@cache/cache";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";
import { assertEdgesNonNull, assertNonNull } from "@helpers/helpers";

import AssessmentSectionSubjectVisibilityBanner from "../assessment/assessment-section-subject-visibility-banner";
import useAssessmentCalibrationData from "../calibration/use-assessment-calibration-data";
import AssessmentLayout from "../layouts/assessment-layout";
import RoleBasedAssessmentQuestionLayout from "../layouts/role-based-assessment-question-layout";
import {
  getQuestionsByRole,
  isCoreValueBasedQuestionType,
  isGoalBasedQuestionType,
  isRoleBasedQuestionType,
} from "../questions/helpers";
import { DeliveryData } from "./assessment-delivery";
import AssessmentDeliveryQuestion from "./assessment-delivery-question";
import RoleBasedAssessmentDeliveryQuestionGroup from "./role-based-assessment-delivery-question-group";

const AssessmentDeliveryContent = ({
  assessmentTemplate,
  assessmentDeliveryData,
  showHiddenQuestions,
  excludedAnswerIds,
  canUpdate,
  isDraft,
  onUpdateDeliveryData,
}: {
  showHiddenQuestions: boolean;
  assessmentTemplate?: AssessmentDeliveryTemplateFragment | null;
  assessmentDeliveryData?: AssessmentDeliveryFragment | null;
  excludedAnswerIds: number[];
  canUpdate: boolean;
  isDraft: boolean;
  onUpdateDeliveryData: (data: Partial<DeliveryData>) => void;
}) => {
  const label = useLabel();

  const { allCalibrations } = useAssessmentCalibrationData({
    complianceProgramId: assessmentDeliveryData?.complianceProgram?.id,
    templateId: assessmentTemplate?.id,
    targetId: assessmentDeliveryData?.target?.id,
  });

  const target = useMemo(
    () => (assessmentDeliveryData ? assessmentDeliveryData.target : null),
    [assessmentDeliveryData]
  );
  const targetCurrentRoleIds = useMemo(
    () => compact(assessmentDeliveryData?.targetCurrentRoleIds),
    [assessmentDeliveryData]
  );

  const assessmentType = useMemo(
    () => assessmentTemplate?.assessmentType,
    [assessmentTemplate]
  );

  const sectionNodes = useMemo(() => {
    if (!assessmentTemplate || !assessmentDeliveryData) {
      return [];
    }
    return getAssessmentSections(
      assessmentTemplate,
      assertNonNull(assessmentDeliveryData.additionalQuestionSets)
    );
  }, [assessmentDeliveryData, assessmentTemplate]);

  const allAnswers = useMemo(() => {
    if (!assessmentDeliveryData) {
      return [];
    }
    const allAnswers = assertEdgesNonNull(assessmentDeliveryData.answers);
    return allAnswers;
  }, [assessmentDeliveryData]);

  const mostRecentAnswers = useMemo(() => {
    if (!assessmentDeliveryData) {
      return [];
    }
    return compact(assessmentDeliveryData.mostRecentAnswers);
  }, [assessmentDeliveryData]);

  const currentUser = currentUserVar();

  const sections = useMemo(
    () =>
      compact(
        sectionNodes.map((section) => {
          const sectionQuestions = section.questions;
          const hasVisibleQuestions = sectionQuestions.some((question) => {
            const answers = allAnswers.filter(
              (answer) => answer.questionId === question.question.id
            );
            return answers.length > 0;
          });
          if (!hasVisibleQuestions) {
            return null;
          }
          const heading =
            String(section.id).endsWith("-goals") ||
            String(section.id).endsWith("-role") ||
            String(section.id).endsWith(
              "-core-values"
            ) ? null : section.title ? (
              <AssessmentLayout.SectionHeading>
                <AssessmentLayout.SectionHeadingTitle>
                  {section.title}
                </AssessmentLayout.SectionHeadingTitle>
                {section.description && !isEmptyValue(section.description) && (
                  <AssessmentLayout.SectionHeadingDescription>
                    <TextareaWysiwyg
                      editable={false}
                      className="bg-white w-full max-w-none"
                      value={section.description}
                    />
                  </AssessmentLayout.SectionHeadingDescription>
                )}
                {target && target?.id !== currentUser.id && (
                  <AssessmentSectionSubjectVisibilityBanner
                    subjectVisibility={section.subjectVisibility}
                    className="mt-2"
                    inDelivery
                  />
                )}
              </AssessmentLayout.SectionHeading>
            ) : null;

          const regularQuestions = sectionQuestions
            .filter(
              ({ question }) =>
                !isRoleBasedQuestionType(question.questionType) &&
                !isGoalBasedQuestionType(question.questionType) &&
                !isCoreValueBasedQuestionType(question.questionType)
            )
            .filter((node) => {
              const questionHidden =
                node.question.responseVisibility ===
                  AssessmentQuestionResponseVisibility.HiddenFromSubject &&
                allAnswers.filter(
                  (answer) =>
                    answer.questionId === node.question.id &&
                    answer.responder?.id === assessmentDeliveryData?.target?.id
                ).length === 0;
              const shown =
                showHiddenQuestions ||
                (!questionHidden &&
                  assessmentTemplate?.delivery !==
                    AssessmentGroupDelivery.None);
              return shown;
            });
          const roleBasedQuestions = sectionQuestions.filter(({ question }) =>
            isRoleBasedQuestionType(question.questionType)
          );
          const goalBasedQuestions = sectionQuestions.filter(({ question }) =>
            isGoalBasedQuestionType(question.questionType)
          );
          const coreValueBasedQuestions = sectionQuestions.filter(
            ({ question }) =>
              isCoreValueBasedQuestionType(question.questionType)
          );
          if (
            regularQuestions.length === 0 &&
            roleBasedQuestions.length === 0 &&
            goalBasedQuestions.length === 0 &&
            coreValueBasedQuestions.length === 0
          ) {
            return null;
          }
          const overallGoalBasedQuestion = goalBasedQuestions.find(
            ({ question }) =>
              question.questionType === AssessmentQuestionType.OverallGoal
          );
          const individualGoalBasedQuestions = goalBasedQuestions.filter(
            ({ question }) =>
              question.questionType === AssessmentQuestionType.IndividualGoal
          );
          const overallGoalBasedQuestionAnswers = allAnswers.filter(
            (answer) =>
              overallGoalBasedQuestion &&
              answer.questionId === overallGoalBasedQuestion.question.id
          );

          const overallCoreValueBasedQuestion = coreValueBasedQuestions.find(
            ({ question }) =>
              question.questionType ===
              AssessmentQuestionType.OverallRecognitionCoreValue
          );
          const individualCoreValueBasedQuestions =
            coreValueBasedQuestions.filter(
              ({ question }) =>
                question.questionType ===
                AssessmentQuestionType.IndividualRecognitionCoreValue
            );
          const overallCoreValueBasedQuestionAnswers = allAnswers.filter(
            (answer) =>
              overallCoreValueBasedQuestion &&
              answer.questionId === overallCoreValueBasedQuestion.question.id
          );

          const {
            roles: currentRoles,
            roleBasedQuestionsByRoleId: currentRoleQuestionsByRoleId,
          } = getQuestionsByRole(roleBasedQuestions, (role) => {
            return targetCurrentRoleIds.includes(role.id);
          });
          const {
            roles: nextRoles,
            roleBasedQuestionsByRoleId: nextRoleQuestionsByRoleId,
          } = getQuestionsByRole(roleBasedQuestions, (role) => {
            return !targetCurrentRoleIds.includes(role.id);
          });

          return (
            <AssessmentLayout.SectionContainer key={section.id}>
              {heading}
              {regularQuestions.length !== 0 && (
                <AssessmentLayout.SectionRegularQuestions>
                  {regularQuestions.map((node) => {
                    const { question } = node;
                    const answers = allAnswers.filter(
                      (answer) =>
                        answer.questionId === question.id &&
                        (showHiddenQuestions ||
                          question.responseVisibility !==
                            AssessmentQuestionResponseVisibility.HiddenFromSubject ||
                          answer.responder?.id ===
                            assessmentDeliveryData?.target?.id)
                    );
                    const calibrations = showHiddenQuestions
                      ? allCalibrations.filter(
                          (c) => c.question.id === question.id
                        )
                      : [];
                    return (
                      <AssessmentDeliveryQuestion
                        key={question.id}
                        calibrations={calibrations}
                        canUpdate={!!canUpdate && isDraft}
                        excludedAnswerIds={excludedAnswerIds}
                        onUpdateExcludedAnswers={(newExcludedAnswerIds) => {
                          onUpdateDeliveryData({
                            excludedAnswerIds: newExcludedAnswerIds,
                          });
                        }}
                        answers={answers}
                        mostRecentAnswers={mostRecentAnswers}
                        question={question}
                        target={target}
                        assessmentType={assessmentType}
                      />
                    );
                  })}
                </AssessmentLayout.SectionRegularQuestions>
              )}
              {map(currentRoleQuestionsByRoleId, (questions, roleIdStr) => {
                const role = assertNonNull(
                  currentRoles.find((role) => role.id === parseInt(roleIdStr))
                );

                return (
                  <RoleBasedAssessmentQuestionLayout.Container key={roleIdStr}>
                    <RoleBasedAssessmentDeliveryQuestionGroup
                      answers={allAnswers.map((ans) => ({
                        ...ans,
                        questionId: ans.questionId,
                        textAnswer:
                          ans.__typename === "TextAssessmentAnswerNode"
                            ? ans.textAnswer
                            : null,
                        comment: ans.comment ?? null,
                      }))}
                      role={role}
                      questions={questions}
                      canUpdate={!!canUpdate && isDraft}
                      excludedAnswerIds={excludedAnswerIds}
                      subjectVisibility={section.subjectVisibility}
                      onUpdateExcludedAnswers={(newExcludedAnswerIds) => {
                        onUpdateDeliveryData({
                          excludedAnswerIds: newExcludedAnswerIds,
                        });
                      }}
                      target={target}
                      isCurrentRole
                    />
                  </RoleBasedAssessmentQuestionLayout.Container>
                );
              })}
              {map(nextRoleQuestionsByRoleId, (questions, roleIdStr) => {
                const role = assertNonNull(
                  nextRoles.find((role) => role.id === parseInt(roleIdStr))
                );
                const hasAnswers = allAnswers.some((answer) =>
                  questions.map((q) => q.id).includes(answer.questionId)
                );
                if (!hasAnswers) {
                  return null;
                }

                return (
                  <RoleBasedAssessmentQuestionLayout.Container key={roleIdStr}>
                    <RoleBasedAssessmentDeliveryQuestionGroup
                      answers={allAnswers.map((ans) => ({
                        ...ans,
                        questionId: ans.questionId,
                        textAnswer:
                          ans.__typename === "TextAssessmentAnswerNode"
                            ? ans.textAnswer
                            : null,
                        comment: ans.comment ?? null,
                      }))}
                      role={role}
                      questions={questions}
                      canUpdate={!!canUpdate && isDraft}
                      subjectVisibility={section.subjectVisibility}
                      excludedAnswerIds={excludedAnswerIds}
                      onUpdateExcludedAnswers={(newExcludedAnswerIds) => {
                        onUpdateDeliveryData({
                          excludedAnswerIds: newExcludedAnswerIds,
                        });
                      }}
                      target={target}
                    />
                  </RoleBasedAssessmentQuestionLayout.Container>
                );
              })}
              {String(section.id).endsWith("-goals") && (
                <>
                  <AssessmentLayout.SectionHeading>
                    <AssessmentLayout.SectionHeadingTitle>
                      {label("goal", { pluralize: true, capitalize: true })}
                    </AssessmentLayout.SectionHeadingTitle>
                    <AssessmentLayout.SectionHeadingDescription>
                      Assess the {label("goal", { pluralize: true })} due in
                      this {label("review")} cycle
                    </AssessmentLayout.SectionHeadingDescription>
                    {target && target?.id !== currentUser.id && (
                      <AssessmentSectionSubjectVisibilityBanner
                        subjectVisibility={section.subjectVisibility}
                        className="mt-2 mb-4"
                        inDelivery
                      />
                    )}
                  </AssessmentLayout.SectionHeading>

                  <AssessmentLayout.SectionRegularQuestions>
                    {individualGoalBasedQuestions.length === 0 && (
                      <div className="p-6">
                        <div className="text-sm text-gray-500">
                          No {label("goal", { pluralize: true })} to assess.
                        </div>
                      </div>
                    )}
                    {individualGoalBasedQuestions
                      .filter((node) => {
                        if (
                          node.question.responseVisibility ===
                            AssessmentQuestionResponseVisibility.HiddenFromSubject &&
                          !showHiddenQuestions
                        )
                          return false;
                        return true;
                      })
                      .map((node) => {
                        const { question } = node;
                        const answers = allAnswers.filter(
                          (answer) => answer.questionId === question.id
                        );
                        return (
                          <AssessmentDeliveryQuestion
                            key={question.id}
                            canUpdate={!!canUpdate && isDraft}
                            excludedAnswerIds={excludedAnswerIds}
                            onUpdateExcludedAnswers={(newExcludedAnswerIds) => {
                              onUpdateDeliveryData({
                                excludedAnswerIds: newExcludedAnswerIds,
                              });
                            }}
                            answers={answers}
                            question={question}
                            target={target}
                            assessmentType={assessmentType}
                          />
                        );
                      })}
                    {individualGoalBasedQuestions.length > 0 &&
                      overallGoalBasedQuestion && (
                        <div>
                          <AssessmentDeliveryQuestion
                            key={overallGoalBasedQuestion.id}
                            canUpdate={!!canUpdate && isDraft}
                            excludedAnswerIds={excludedAnswerIds}
                            onUpdateExcludedAnswers={(newExcludedAnswerIds) => {
                              onUpdateDeliveryData({
                                excludedAnswerIds: newExcludedAnswerIds,
                              });
                            }}
                            answers={overallGoalBasedQuestionAnswers}
                            question={overallGoalBasedQuestion.question}
                            target={target}
                            assessmentType={assessmentType}
                          />
                        </div>
                      )}
                  </AssessmentLayout.SectionRegularQuestions>
                </>
              )}
              {String(section.id).endsWith("-core-values") && (
                <>
                  <AssessmentLayout.SectionHeading>
                    <AssessmentLayout.SectionHeadingTitle>
                      Core Values
                    </AssessmentLayout.SectionHeadingTitle>
                    <AssessmentLayout.SectionHeadingDescription>
                      Assess alignment with Core Values in this{" "}
                      {label("review")} cycle
                    </AssessmentLayout.SectionHeadingDescription>
                    {target && target?.id !== currentUser.id && (
                      <AssessmentSectionSubjectVisibilityBanner
                        subjectVisibility={section.subjectVisibility}
                        className="mt-2 mb-4"
                        inDelivery
                      />
                    )}
                  </AssessmentLayout.SectionHeading>

                  <AssessmentLayout.SectionRegularQuestions>
                    {individualCoreValueBasedQuestions.length === 0 && (
                      <div className="p-6">
                        <div className="text-sm text-gray-500">
                          No Core Values to assess.
                        </div>
                      </div>
                    )}
                    {individualCoreValueBasedQuestions
                      .filter((node) => {
                        if (
                          node.question.responseVisibility ===
                            AssessmentQuestionResponseVisibility.HiddenFromSubject &&
                          !showHiddenQuestions
                        )
                          return false;
                        return true;
                      })
                      .map((node) => {
                        const { question } = node;
                        const answers = allAnswers.filter(
                          (answer) => answer.questionId === question.id
                        );
                        return (
                          <AssessmentDeliveryQuestion
                            key={question.id}
                            canUpdate={!!canUpdate && isDraft}
                            excludedAnswerIds={excludedAnswerIds}
                            onUpdateExcludedAnswers={(newExcludedAnswerIds) => {
                              onUpdateDeliveryData({
                                excludedAnswerIds: newExcludedAnswerIds,
                              });
                            }}
                            answers={answers}
                            question={question}
                            target={target}
                            periodStartDate={
                              assessmentDeliveryData?.complianceProgram
                                ?.periodStartDate
                            }
                            periodEndDate={
                              assessmentDeliveryData?.complianceProgram
                                ?.periodEndDate
                            }
                            assessmentType={assessmentType}
                          />
                        );
                      })}
                    {individualCoreValueBasedQuestions.length > 0 &&
                      overallCoreValueBasedQuestion && (
                        <div>
                          <AssessmentDeliveryQuestion
                            key={overallCoreValueBasedQuestion.id}
                            canUpdate={!!canUpdate && isDraft}
                            excludedAnswerIds={excludedAnswerIds}
                            onUpdateExcludedAnswers={(newExcludedAnswerIds) => {
                              onUpdateDeliveryData({
                                excludedAnswerIds: newExcludedAnswerIds,
                              });
                            }}
                            answers={overallCoreValueBasedQuestionAnswers}
                            question={overallCoreValueBasedQuestion.question}
                            target={target}
                            assessmentType={assessmentType}
                          />
                        </div>
                      )}
                  </AssessmentLayout.SectionRegularQuestions>
                </>
              )}
            </AssessmentLayout.SectionContainer>
          );
        })
      ),
    [
      assessmentTemplate,
      assessmentDeliveryData,
      showHiddenQuestions,
      excludedAnswerIds,
      canUpdate,
      isDraft,
      onUpdateDeliveryData,
      sectionNodes,
      allAnswers,
      allCalibrations,
      target,
      currentUser,
      targetCurrentRoleIds,
      label,
      mostRecentAnswers,
      assessmentType,
    ]
  );

  // In parent component, we can check if this returns null to hide the assessment type.
  if (sections.length === 0) {
    return null;
  }

  return sections;
};

export default AssessmentDeliveryContent;
